import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  MenuItem,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import { updateCustomer } from '../../services/api';
import { formatKenyanPhone, isValidKenyanPhone } from '../../utils/phoneUtils';

const EditCustomerModal = ({ open, onClose, customer, onUpdate, companies, isSuperAdmin, isCusAdmin, currentUserCompanyId }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '+254',
    address: '',
    type: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [formValid, setFormValid] = useState(true);

  // Reset all state when modal closes
  const handleClose = () => {
    setError('');
    setLoading(false);
    setPhoneError('');
    setFormData({
      name: '',
      phone: '+254',
      address: '',
      type: 1
    });
    onClose();
  };

  useEffect(() => {
    if (customer && open) {
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '+254',
        address: customer.address || '',
        type: customer.type || 1
      });
      setError('');
      setPhoneError('');
    }
  }, [customer, open]);

  // Validate form
  useEffect(() => {
    const validateForm = () => {
      const requiredFieldsValid = formData.name && formData.phone;
      const phoneValid = formData.phone && isValidKenyanPhone(formData.phone);
      setPhoneError(phoneValid ? '' : 'Phone must be in format +254XXXXXXXXX');
      setFormValid(requiredFieldsValid && phoneValid);
    };
    
    validateForm();
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const formattedPhone = formatKenyanPhone(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) return;
    
    setLoading(true);
    setError('');

    try {
      const response = await updateCustomer(customer.id, formData);
      onUpdate(response.data.customer);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  if (!customer) return null;

  return (
    <AppDialog open={open} onClose={handleClose}>
      <AppDialogTitle onClose={handleClose} subtitle="Update customer information">
        Edit Customer
      </AppDialogTitle>
      <form onSubmit={handleSubmit}>
        <AppDialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField
            fullWidth
            required
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
          />
          
          <TextField
            fullWidth
            required
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            error={!!phoneError}
            helperText={phoneError || "Format: +254XXXXXXXXX"}
            margin="normal"
            InputProps={{
              startAdornment: formData.phone && !formData.phone.startsWith('+') ? (
                <InputAdornment position="start">+</InputAdornment>
              ) : null
            }}
          />
          
          <TextField
            fullWidth
            label="Address (optional)"
            name="address"
            value={formData.address}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={3}
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="type-select-label">Type</InputLabel>
            <Select
              labelId="type-select-label"
              name="type"
              value={formData.type}
              onChange={handleChange}
              label="Type"
              required
            >
              <MenuItem value={1}>Individual</MenuItem>
              <MenuItem value={2}>Company</MenuItem>
            </Select>
          </FormControl>
        </AppDialogContent>
        
        <AppDialogActions>
          <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !formValid}
          >
            {loading ? 'Updating...' : 'Update Customer'}
          </Button>
        </AppDialogActions>
      </form>
    </AppDialog>
  );
};

export default EditCustomerModal;

